<template>
  <b-card class="p-1">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="d-flex align-items-center">
        <b-form-group class="w-auto mb-0 mr-3 lang-select">
          <label for="language-select" class="small">Język:</label>
          <v-select
            id="language-select"
            label="text"
            placeholder="Wybierz język"
            v-model="selectedLanguage"
            :clearable="false"
            :deselectFromDropdown="false"
            :options="availableLanguages"
            @input="filterByLanguage" />
        </b-form-group>

        <b-form-group class="w-auto mb-0">
          <label for="filterInput" class="small">Wyszukaj:</label>
          <b-input-group>
            <b-form-input id="filterInput" v-model="filter" type="search" placeholder="Wyszukaj po tytule" />
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''"> Wyczyść </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>

      <b-button
        :to="{ name: 'navigation-management-create' }"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        type="submit"
        variant="primary"
        class="btn">
        Dodaj
      </b-button>
    </div>

    <b-table
      responsive
      bordered
      striped
      hover
      :items="filteredNavigationLinks"
      :fields="fields"
      :filter="filter"
      :filter-included-fields="filterOn">
      <template #cell(parent)="row">
        <b-link :to="{ name: 'navigation-management-edit', params: { id: row.item.parent.id } }" v-if="row.item.parent">
          {{ row.item.parent.title }}
        </b-link>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell(link_type)="row">
        <b-badge :variant="row.item.link_type === 'internal' ? 'success' : 'warning'">
          {{ row.item.link_type === 'internal' ? 'Wewnętrzny' : 'Zewnętrzny' }}
        </b-badge>
      </template>

      <template #cell(url)="row">
        <span v-if="row.item.link_type === 'internal'">{{ row.item.route_name }}</span>
        <a v-else :href="row.item.external_url" target="_blank" class="text-primary">
          {{ row.item.external_url }}
        </a>
      </template>

      <template #cell(is_active)="row">
        <b-badge :variant="row.item.is_active ? 'success' : 'danger'">
          {{ row.item.is_active ? 'Aktywny' : 'Nieaktywny' }}
        </b-badge>
      </template>

      <template #cell(actions)="row">
        <div class="d-flex flex-column justify-content-center align-items-center">
          <b-button
            :to="{ name: 'navigation-management-edit', params: { id: row.item.id } }"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            class="btn table-btn"
            size="sm">
            Edytuj
          </b-button>
          <b-button class="mt-1 table-btn" variant="danger" size="sm" @click="deleteNavigationLink(row.item)">
            Usuń
          </b-button>
        </div>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';
import { all, destroy } from '@/http/navigation-management';
import { LanguageFormatter } from '@/formatters/LanguageFormatter';
import { getLanguages } from '@/constants/languages';

export default {
  mixins: [NotificationMixin],

  data() {
    return {
      navigationLinks: [],
      filteredNavigationLinks: [],
      selectedLanguage: { text: 'Polski', value: 'pl' },
      filter: '',
      filterOn: ['title'],

      availableLanguages: getLanguages().map((lang) => ({
        text: lang.value,
        value: lang.lang,
      })),

      fields: [
        {
          key: 'title',
          label: 'Tytuł',
          sortable: true,
          sortDirection: 'desc',
        },
        {
          key: 'lang',
          label: 'Język',
          sortable: true,
          formatter: (val) => LanguageFormatter(val),
        },
        {
          key: 'parent',
          label: 'Link nadrzędny',
          sortable: false,
        },
        {
          key: 'link_type',
          label: 'Typ',
          sortable: true,
        },
        {
          key: 'url',
          label: 'URL / Nazwa podstrony',
          sortable: false,
        },
        {
          key: 'sort_order',
          label: 'Kolejność',
          sortable: true,
        },
        {
          key: 'is_active',
          label: 'Status',
          sortable: true,
        },
        { key: 'actions', label: 'Akcje', class: 'w-25' },
      ],
    };
  },

  async created() {
    await this.fetchNavigationLinks();
  },

  methods: {
    async fetchNavigationLinks() {
      try {
        const { data } = await all();
        this.navigationLinks = data.data;
        this.filterByLanguage();
      } catch (error) {
        this.showErrorNotification(
          'Problem z pobraniem danych',
          'Wystąpił problem z pobraniem linków nawigacji. Skontaktuj się ze swoim developerem.',
        );
      }
    },

    filterByLanguage() {
      let filtered = this.navigationLinks;

      // Filter by language
      if (this.selectedLanguage) {
        filtered = filtered.filter((link) => link.lang === this.selectedLanguage.value);
      }

      this.filteredNavigationLinks = filtered;
    },

    async deleteNavigationLink(navigationLink) {
      this.$swal({
        title: 'Jesteś pewny?',
        text: 'Czy chcesz usunąć ten link nawigacji?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          destroy(navigationLink.id)
            .then(() => {
              this.showSuccessNotification('Usunięto link', 'Link nawigacji został usunięty.');
              this.fetchNavigationLinks();
            })
            .catch(() => {
              this.showErrorNotification(
                'Problem z usunięciem linku',
                'Wystąpił problem z usunięciem linku nawigacji. Skontaktuj się ze swoim developerem.',
              );
            });
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.table-btn {
  min-width: 80px;
}

.lang-select {
  min-width: 140px;
  width: auto;
}
</style>
